import {
  FF_MANDATORY_ACCOUNT_CHINA,
  FF_MANDATORY_ACCOUNT_CHINA_VERSION,
  FF_MANDATORY_ACCOUNT_INDIA,
  FF_MANDATORY_ACCOUNT_INDIA_VERSION,
  FeatureFlag,
} from 'constants/features';
import { PAYEX_PLATFORM } from 'constants/ui';
import { usePeertransferFlags } from 'hooks/usePeertransferFlags/usePeertransferFlags';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { paymentIsFromAgents } from 'selectors/entities/order/order';
import {
  getPaymentGroup,
  getRecipientCurrencyCode,
  getRecipientId,
} from 'selectors/entities/recipient/recipient';
import {
  getPlatform,
  isPayingFromChina,
  isPayingFromIndia,
} from 'selectors/ui/ui';
import {
  type TestVariant,
  getTestVariant,
} from 'utils/getTestVariant/getTestVariant';

type Eligibility = {
  india: boolean;
  china: boolean;
};

type TestVariants = {
  india: TestVariant;
  china: TestVariant;
};

export type UseMandatoryAccountEligibilityResult = {
  eligibility: Eligibility;
  testVariants: TestVariants;
  isFetching: boolean;
};

const REFETCH_TIMEOUT = 60000;

/**
 * List of recipient currencies that are excluded from the China mandatory account experiment.
 * Recipients with these currencies will not be eligible for the China experiment,
 * regardless of other eligibility criteria.
 */
export const DISCARDED_RECIPIENT_CURRENCIES = [
  'SGD',
  'JPY',
  'KRW',
  'MYR',
  'HKD',
];

/**
 * List of recipient IDs that are excluded from the mandatory account experiment.
 * To exclude specific recipients from the experiment, add their IDs to this array.
 * Recipients with IDs included in this list will not be eligible for either
 * India or China experiments, regardless of other eligibility criteria.
 *
 * @example ['ARA', 'DISCARDED_RECIPIENT']
 */
export const DISCARDED_RECIPIENT_IDS: string[] = [];

/**
 * List of payment groups that are eligible for the mandatory account experiment.
 * Only payments with these group types will be considered for the experiment.
 */
export const PAYMENTS_GROUP = ['ITP', 'OTP', 'DOM'];

const FEATURE_FLAGS: FeatureFlag[] = [
  FF_MANDATORY_ACCOUNT_INDIA,
  FF_MANDATORY_ACCOUNT_INDIA_VERSION,
  FF_MANDATORY_ACCOUNT_CHINA,
  FF_MANDATORY_ACCOUNT_CHINA_VERSION,
];

const calculateEligibility = (
  isFromAgents: boolean,
  isPayexPlatform: boolean,
  payingFromIndia: boolean,
  payingFromChina: boolean,
  recipientCurrency: string,
  paymentGroup: string,
  recipientId: string,
): Eligibility => {
  const baseEligibility =
    !isFromAgents &&
    isPayexPlatform &&
    PAYMENTS_GROUP.includes(paymentGroup) &&
    !DISCARDED_RECIPIENT_IDS.includes(recipientId);

  return {
    india: baseEligibility && payingFromIndia,
    china:
      baseEligibility &&
      payingFromChina &&
      !DISCARDED_RECIPIENT_CURRENCIES.includes(recipientCurrency),
  };
};

export const useMandatoryAccountEligibility =
  (): UseMandatoryAccountEligibilityResult => {
    const isFromAgents = useSelector(paymentIsFromAgents);
    const platform = useSelector(getPlatform);
    const payingFromIndia = useSelector(isPayingFromIndia);
    const payingFromChina = useSelector(isPayingFromChina);
    const recipientCurrency = useSelector(getRecipientCurrencyCode);
    const recipientId = useSelector(getRecipientId);
    const paymentGroup = useSelector(getPaymentGroup);

    const isPayexPlatform = platform === PAYEX_PLATFORM;

    const eligibility = useMemo(
      () =>
        calculateEligibility(
          isFromAgents,
          isPayexPlatform,
          payingFromIndia,
          payingFromChina,
          recipientCurrency ?? '',
          paymentGroup ?? '',
          recipientId,
        ),
      [
        isFromAgents,
        isPayexPlatform,
        payingFromIndia,
        payingFromChina,
        recipientCurrency,
        paymentGroup,
        recipientId,
      ],
    );

    const shouldFetchFlags = eligibility.india || eligibility.china;

    const { data: featureFlags } = usePeertransferFlags(FEATURE_FLAGS, {
      refetchInterval: REFETCH_TIMEOUT,
      enabled: shouldFetchFlags,
    });

    const testVariants = useMemo(
      () => ({
        india: getTestVariant(
          featureFlags?.[FF_MANDATORY_ACCOUNT_INDIA] ?? false,
          featureFlags?.[FF_MANDATORY_ACCOUNT_INDIA_VERSION] ?? false,
        ),
        china: getTestVariant(
          featureFlags?.[FF_MANDATORY_ACCOUNT_CHINA] ?? false,
          featureFlags?.[FF_MANDATORY_ACCOUNT_CHINA_VERSION] ?? false,
        ),
      }),
      [featureFlags],
    );
    const isFetching = featureFlags === undefined;

    return { eligibility, testVariants, isFetching };
  };
